import { useState, useEffect, useRef } from "react";
import { Input } from "@/components";
import { FormCard } from "@/components/FormCard";
import { Copy, CopyCheck, ArrowRight, Download, Phone, MessageSquare, QrCode, Share2 } from "lucide-react";
import QRCodeStyling from "qr-code-styling";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/utils/cn";

// ─── QR Style Thumbnail Components ────────────────────────────────────────────
// These are small visual previews of each card style shown in the style picker

function ThumbRounded({ active }: { active?: boolean }) {
  return (
    <div className={cn("flex flex-col items-center p-2 rounded-xl border-2 w-full h-full", active ? "border-ui-success bg-ui-success/5" : "border-ui-hairline bg-ui-surface-soft")}>
      <div className="w-8 h-8 bg-ui-surface-card rounded-lg border border-ui-hairline flex items-center justify-center">
        <QRIcon size={18} color={active ? "#10b981" : "#94a3b8"} />
      </div>
      <div className={cn("mt-1.5 px-2 py-0.5 rounded-full text-[7px] font-bold", active ? "bg-ui-success text-white" : "bg-ui-muted/30 text-ui-muted")}>SCAN ME</div>
    </div>
  );
}

function ThumbSquare({ active }: { active?: boolean }) {
  return (
    <div className={cn("flex flex-col items-center p-2 border-[3px] w-full h-full", active ? "border-ui-ink bg-white" : "border-ui-body/40 bg-ui-surface-soft")}>
      <div className="w-8 h-8 bg-white border border-ui-hairline flex items-center justify-center">
        <QRIcon size={18} color={active ? "#0f172a" : "#94a3b8"} />
      </div>
      <div className={cn("mt-1.5 px-2 py-0.5 text-[7px] font-bold", active ? "bg-ui-ink text-white" : "bg-ui-muted/30 text-ui-muted")}>SCAN</div>
    </div>
  );
}

function ThumbBubble({ active }: { active?: boolean }) {
  return (
    <div className="relative flex flex-col items-center w-full h-full">
      <div className={cn("flex flex-col items-center p-2 rounded-xl w-full", active ? "bg-ui-success" : "bg-ui-muted/30")}>
        <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center">
          <QRIcon size={18} color={active ? "#10b981" : "#94a3b8"} />
        </div>
        <div className={cn("mt-1 text-[7px] font-bold", active ? "text-white" : "text-ui-muted")}>SCAN ME</div>
      </div>
      <div className={cn("w-3 h-3 rotate-45 -mt-1.5", active ? "bg-ui-success" : "bg-ui-muted/30")} />
    </div>
  );
}

function ThumbScanCard({ active }: { active?: boolean }) {
  return (
    <div className={cn("flex items-center rounded-lg overflow-hidden w-full h-full border", active ? "border-ui-ink" : "border-ui-hairline")}>
      <div className={cn("flex flex-col justify-center items-center px-1.5 py-2 text-center shrink-0", active ? "bg-ui-ink text-white" : "bg-ui-muted/20 text-ui-muted")}>
        <div className="text-[6px] font-black leading-none">SCAN</div>
        <div className="text-[6px] font-black leading-none">ME</div>
      </div>
      <div className="flex-1 bg-ui-surface-soft flex items-center justify-center p-1">
        <QRIcon size={18} color={active ? "#0f172a" : "#94a3b8"} />
      </div>
    </div>
  );
}

function ThumbBadge({ active }: { active?: boolean }) {
  return (
    <div className="relative flex flex-col items-center justify-end w-full h-full pt-3">
      <div className={cn("absolute top-0 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full text-[6px] font-bold z-10 whitespace-nowrap", active ? "bg-ui-warning text-white" : "bg-ui-muted/30 text-ui-muted")}>SCAN ME</div>
      <div className={cn("flex items-center justify-center p-1.5 rounded-xl border-2 w-full mt-2", active ? "border-ui-warning bg-yellow-50" : "border-ui-hairline bg-ui-surface-soft")}>
        <QRIcon size={18} color={active ? "#f59e0b" : "#94a3b8"} />
      </div>
    </div>
  );
}

function ThumbCorners({ active }: { active?: boolean }) {
  return (
    <div className="relative flex items-center justify-center w-full h-full p-2">
      <div className={cn("absolute top-0 left-0 w-3 h-3 border-t-2 border-l-2", active ? "border-ui-ink" : "border-ui-muted")} />
      <div className={cn("absolute top-0 right-0 w-3 h-3 border-t-2 border-r-2", active ? "border-ui-ink" : "border-ui-muted")} />
      <div className={cn("absolute bottom-0 left-0 w-3 h-3 border-b-2 border-l-2", active ? "border-ui-ink" : "border-ui-muted")} />
      <div className={cn("absolute bottom-0 right-0 w-3 h-3 border-b-2 border-r-2", active ? "border-ui-ink" : "border-ui-muted")} />
      <QRIcon size={20} color={active ? "#0f172a" : "#94a3b8"} />
    </div>
  );
}

function ThumbRibbon({ active }: { active?: boolean }) {
  return (
    <div className="relative flex flex-col items-center w-full h-full pb-2">
      <div className={cn("flex items-center justify-center p-1.5 rounded-lg border-[3px] w-full", active ? "border-ui-error" : "border-ui-hairline")}>
        <QRIcon size={18} color={active ? "#ef4444" : "#94a3b8"} />
      </div>
      <div className={cn("mt-0.5 text-[6px] font-bold px-2 py-0.5 rounded-sm", active ? "bg-ui-error text-white" : "bg-ui-muted/20 text-ui-muted")}>SCAN ME</div>
    </div>
  );
}

function QRIcon({ size = 16, color = "#94a3b8" }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="2" y="2" width="8" height="8" rx="1" stroke={color} strokeWidth="1.5" fill="none" />
      <rect x="4" y="4" width="4" height="4" rx="0.3" fill={color} />
      <rect x="14" y="2" width="8" height="8" rx="1" stroke={color} strokeWidth="1.5" fill="none" />
      <rect x="16" y="4" width="4" height="4" rx="0.3" fill={color} />
      <rect x="2" y="14" width="8" height="8" rx="1" stroke={color} strokeWidth="1.5" fill="none" />
      <rect x="4" y="16" width="4" height="4" rx="0.3" fill={color} />
      <rect x="14" y="14" width="2" height="2" fill={color} />
      <rect x="17" y="14" width="2" height="2" fill={color} />
      <rect x="20" y="14" width="2" height="2" fill={color} />
      <rect x="14" y="17" width="2" height="2" fill={color} />
      <rect x="20" y="17" width="2" height="2" fill={color} />
      <rect x="17" y="20" width="2" height="2" fill={color} />
      <rect x="20" y="20" width="2" height="2" fill={color} />
    </svg>
  );
}

// ─── Step Badge ────────────────────────────────────────────────────────────────
function StepBadge({ n }: { n: number }) {
  return (
    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-ui-success/15 text-ui-success text-xs font-bold border border-ui-success/30 shrink-0">
      {n}
    </span>
  );
}

// ─── QR Style Data ─────────────────────────────────────────────────────────────
const QR_THEMES = [
  { id: "rounded", label: "Rounded", Thumb: ThumbRounded },
  { id: "square", label: "Square", Thumb: ThumbSquare },
  { id: "bubble-bottom", label: "Bubble Bottom", Thumb: ThumbBubble },
  { id: "scan-card", label: "Scan Card", Thumb: ThumbScanCard },
  { id: "badge", label: "Badge", Thumb: ThumbBadge },
  { id: "corners", label: "Corners", Thumb: ThumbCorners },
  { id: "qr-ribbon", label: "QR Ribbon", Thumb: ThumbRibbon },
];

const QR_SIZE = 200; // exact pixel match for qr-code-styling output

// ─── How It Works steps ────────────────────────────────────────────────────────
const HOW_IT_WORKS = [
  { icon: Phone, text: "Enter your WhatsApp number with country code" },
  { icon: MessageSquare, text: "Optionally add a message that auto-fills for visitors" },
  { icon: QrCode, text: "Generate your link and QR code instantly" },
  { icon: Share2, text: "Share the link or download the QR code" },
];

// ─── QR Card Frame Previews (large, live preview) ──────────────────────────────
function QRCardFrame({ themeId, qrImageUrl }: { themeId: string; qrImageUrl: string }) {
  const qrImg = qrImageUrl ? (
    <img
      src={qrImageUrl}
      alt="QR Code"
      style={{ width: QR_SIZE, height: QR_SIZE, display: "block", objectFit: "contain" }}
    />
  ) : (
    <div style={{ width: QR_SIZE, height: QR_SIZE }} className="bg-ui-surface-soft rounded" />
  );

  if (themeId === "rounded") return (
    <div className="bg-gradient-to-br from-ui-success/80 to-emerald-400 p-[3px] rounded-[35px] shadow-xl">
      <div className="bg-white rounded-[30px] p-5 flex flex-col items-center gap-3">
        <div className="bg-white p-3 rounded-xl shadow-inner">{qrImg}</div>
        <div className="bg-ui-success text-white px-8 py-2 rounded-full font-bold tracking-wider text-sm">SCAN ME</div>
      </div>
    </div>
  );

  if (themeId === "square") return (
    <div className="border-[6px] border-ui-ink p-5 flex flex-col items-center gap-3 bg-white shadow-lg">
      <div className="p-2 bg-white">{qrImg}</div>
      <div className="bg-ui-ink text-white px-6 py-2 font-bold text-center text-sm">SCAN ME</div>
    </div>
  );

  if (themeId === "bubble-bottom") return (
    <div className="relative bg-ui-success text-white p-5 rounded-2xl flex flex-col items-center shadow-xl mb-3">
      <div className="bg-white p-3 rounded-lg">{qrImg}</div>
      <div className="mt-3 font-bold tracking-wide text-sm">SCAN ME</div>
      <div className="absolute -bottom-3 w-6 h-6 bg-ui-success rotate-45" />
    </div>
  );

  if (themeId === "scan-card") return (
    <div className="flex items-stretch bg-white rounded-2xl overflow-hidden shadow-lg border border-ui-hairline w-fit">
      <div className="bg-ui-ink text-white px-5 flex flex-col justify-center items-center text-center">
        <div className="text-xs tracking-widest mb-1">► ► ►</div>
        <div className="text-xl font-bold leading-none">SCAN</div>
        <div className="text-2xl font-extrabold leading-none">ME</div>
        <div className="text-xs tracking-widest mt-1">► ► ►</div>
      </div>
      <div className="w-px bg-ui-hairline" />
      <div className="bg-ui-surface-soft px-4 py-4 flex items-center justify-center">
        <div className="bg-white p-3 rounded-lg shadow-inner">{qrImg}</div>
      </div>
    </div>
  );

  if (themeId === "badge") return (
    <div className="relative bg-yellow-50 border-2 border-yellow-400 p-5 rounded-2xl shadow-xl flex flex-col items-center mt-5">
      <div className="absolute -top-4 bg-yellow-400 text-black px-4 py-1 rounded-full text-sm font-bold shadow">SCAN ME</div>
      <div className="bg-white p-3 rounded-lg shadow-inner">{qrImg}</div>
    </div>
  );

  if (themeId === "corners") return (
    <div className="relative p-8 bg-white">
      <div className="bg-white p-3">{qrImg}</div>
      <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-ui-ink" />
      <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-ui-ink" />
      <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-ui-ink" />
      <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-ui-ink" />
      <div className="text-center mt-2 font-bold text-sm text-ui-ink">SCAN ME</div>
    </div>
  );

  // qr-ribbon
  return (
    <div className="flex justify-center pb-12">
      <div className="relative flex flex-col items-center">
        <div className="relative bg-white p-5 rounded-3xl border-[10px] border-ui-error shadow-lg">
          <div className="bg-white p-2">{qrImg}</div>
          <div className="absolute top-3 left-3 w-6 h-6 border-l-4 border-t-4 border-ui-ink" />
          <div className="absolute top-3 right-3 w-6 h-6 border-r-4 border-t-4 border-ui-ink" />
          <div className="absolute bottom-3 left-3 w-6 h-6 border-l-4 border-b-4 border-ui-ink" />
          <div className="absolute bottom-3 right-3 w-6 h-6 border-r-4 border-b-4 border-ui-ink" />
        </div>
        <div className="absolute -bottom-14 left-1/2 -translate-x-1/2">
          <svg width="260" height="80" viewBox="0 0 260 80">
            <polygon points="8,44 65,28 65,60" fill="#ef4444" />
            <polygon points="252,44 195,28 195,60" fill="#ef4444" />
            <path d="M65 36 Q130 12 195 36 L195 60 Q130 72 65 60 Z" fill="#ef4444" />
            <text x="130" y="52" textAnchor="middle" fontSize="16" fill="white" fontWeight="700" letterSpacing="2">SCAN ME</text>
          </svg>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
export default function WhatsAppQRGenerator() {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [message, setMessage] = useState("");
  const [generatedLink, setGeneratedLink] = useState("");
  const [copied, setCopied] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const [selectedThemeId, setSelectedThemeId] = useState("rounded");
  const [qrImageUrl, setQrImageUrl] = useState("");

  const qrCode = useRef<QRCodeStyling | null>(null);

  useEffect(() => {
    qrCode.current = new QRCodeStyling({
      width: QR_SIZE,
      height: QR_SIZE,
      data: "",
      margin: 8,
      dotsOptions: { color: "#0a0a0a", type: "rounded" },
      backgroundOptions: { color: "#ffffff" },
      cornersSquareOptions: { type: "extra-rounded", color: "#000000" },
      cornersDotOptions: { type: "dot", color: "#000000" },
      imageOptions: { crossOrigin: "anonymous" },
    });
  }, []);

  useEffect(() => {
    if (!generatedLink || !qrCode.current) return;
    qrCode.current.update({ data: generatedLink });
    qrCode.current.getRawData("png").then((rawData) => {
      if (rawData) {
        if (qrImageUrl) URL.revokeObjectURL(qrImageUrl);
        const blob = rawData instanceof Blob ? rawData : new Blob([rawData as any]);
        setQrImageUrl(URL.createObjectURL(blob));
      }
    });
  }, [generatedLink]);

  const downloadQR = () => {
    qrCode.current?.download({ name: "whatsapp-qr", extension: "png" });
  };

  const generateLink = () => {
    if (!phoneNumber.trim()) {
      setPhoneError("Phone number is required");
      return;
    }
    setPhoneError("");
    const phone = phoneNumber.replace(/[^\d]/g, "");
    const msg = message || "Hello! I'd like to connect with you...";
    setGeneratedLink(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`);
  };

  const copyLink = () => {
    if (!generatedLink) return;
    navigator.clipboard.writeText(generatedLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const canGenerate = phoneNumber.trim().length > 0;

  return (
    <div>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">

        {/* ── LEFT COLUMN ── */}
        <div className="space-y-4 lg:col-span-5">

          {/* Step 1 — Enter details */}
          <FormCard
            title={<span className="flex items-center gap-2"><StepBadge n={1} /> Enter your details</span>}
          >
            <div className="space-y-4">
              {/* Phone */}
              <div className="space-y-1.5">
                <label htmlFor="qr-phone" className="flex items-center gap-1.5 text-xs font-semibold text-ui-body">
                  <Phone size={12} className="text-ui-muted" /> WhatsApp Number
                </label>
                <Input
                  id="qr-phone"
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => {
                    setPhoneNumber(e.target.value);
                    if (e.target.value.trim()) setPhoneError("");
                  }}
                  onKeyDown={(e) => {
                    if (!/[0-9]/.test(e.key) && !["Backspace", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key))
                      e.preventDefault();
                  }}
                  placeholder="+1 234 567 8900"
                  className="w-full h-9 text-sm"
                />
                <p className="text-[10px] text-ui-muted">Include country code, e.g. +1 for US, +44 for UK</p>
                {phoneError && <p className="text-[10px] text-ui-error font-medium">{phoneError}</p>}
              </div>

              {/* Message */}
              <div className="space-y-1.5">
                <label htmlFor="qr-message" className="flex items-center gap-1.5 text-xs font-semibold text-ui-body">
                  <MessageSquare size={12} className="text-ui-muted" />
                  Pre-filled Message
                  <span className="text-[10px] font-normal text-ui-muted bg-ui-surface-soft px-1.5 py-0.5 rounded-sm border border-ui-hairline">Optional</span>
                </label>
                <textarea
                  id="qr-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Hello! I'd like to connect with you..."
                  rows={3}
                  className="w-full px-3 py-2 border border-ui-hairline rounded-sm focus:ring-1 focus:ring-ui-primary focus:border-ui-primary bg-ui-surface-card text-sm text-ui-ink resize-none"
                />
                <p className="text-[10px] text-ui-muted">This message will appear pre-typed when someone opens your link.</p>
              </div>

              {/* Generate Button */}
              <button
                type="button"
                onClick={generateLink}
                disabled={!canGenerate}
                className={cn(
                  "w-full flex items-center justify-center gap-2 py-2.5 rounded-sm font-semibold text-sm transition-all",
                  canGenerate
                    ? "bg-ui-success text-white hover:bg-emerald-600"
                    : "bg-ui-surface-soft text-ui-muted border border-ui-hairline cursor-not-allowed"
                )}
              >
                <QrCode size={16} />
                Generate Link &amp; QR Code
                <ArrowRight size={14} />
              </button>
            </div>
          </FormCard>


          {/* How it works */}
          <FormCard title="How it works">
            <div className="space-y-3">
              {HOW_IT_WORKS.map(({ icon: Icon, text }, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-sm bg-ui-success/10 border border-ui-success/20 flex items-center justify-center shrink-0 mt-0.5">
                    <Icon size={10} className="text-ui-success" />
                  </div>
                  <p className="text-xs text-ui-body leading-relaxed">{text}</p>
                </div>
              ))}
            </div>
          </FormCard>
        </div>

        {/* ── RIGHT COLUMN ── */}
        <div className="space-y-4 lg:col-span-7 lg:sticky lg:top-0">

          {/* Step 3 — QR Preview */}
          <FormCard
            title={
              <span className="flex items-center gap-2">
                {generatedLink ? <StepBadge n={2} /> : null}
                QR Code Preview
              </span>
            }
            action={
              generatedLink ? (
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-ui-error" />
                  <span className="w-2 h-2 rounded-full bg-ui-warning" />
                  <span className="w-2 h-2 rounded-full bg-ui-success" />
                  <span className="text-[10px] font-bold text-ui-muted ml-1 uppercase tracking-wider">LIVE</span>
                </div>
              ) : undefined
            }
          >
            {generatedLink ? (
              <div className="flex flex-col md:flex-row gap-4">
                {/* Left Side: Fixed size Preview Container to prevent shifting */}
                <div className="flex-1 min-w-[260px] h-[340px] bg-ui-surface-soft/40 border border-ui-hairline rounded-sm flex items-center justify-center p-4 relative overflow-hidden shrink-0">
                  <motion.div
                    key={selectedThemeId}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ type: "spring", stiffness: 300, damping: 25 }}
                    className="flex items-center justify-center w-full h-full scale-[0.8] sm:scale-[0.85] md:scale-95"
                  >
                    <QRCardFrame themeId={selectedThemeId} qrImageUrl={qrImageUrl} />
                  </motion.div>
                </div>

                {/* Right Side: Style Selection */}
                <div className="flex-1 flex flex-col justify-between min-w-0">
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <StepBadge n={4} />
                      <span className="text-xs font-semibold text-ui-ink">Choose a style</span>
                    </div>
                    <div className="grid md:grid-cols-3 lg:grid-cols-2 xl:grid-cols-3 gap-2">
                      {QR_THEMES.map(({ id, label, Thumb }) => (
                        <button
                          key={id}
                          type="button"
                          onClick={() => setSelectedThemeId(id)}
                          className={cn(
                            "group relative flex flex-col items-center gap-1 p-1 rounded-sm border transition-all",
                            selectedThemeId === id
                              ? "border-ui-success bg-ui-success/5 ring-1 ring-ui-success/30"
                              : "border-ui-hairline bg-ui-surface-card hover:border-ui-success/50"
                          )}
                        >
                          <div className="w-full h-11 overflow-hidden">
                            <Thumb active={selectedThemeId === id} />
                          </div>
                          <span className={cn("text-[8px] font-semibold leading-tight text-center mt-0.5", selectedThemeId === id ? "text-ui-success" : "text-ui-muted")}>
                            {label}
                          </span>
                          {selectedThemeId === id && (
                            <span className="absolute -top-1 -right-1 w-3 h-3 bg-ui-success rounded-full flex items-center justify-center text-[7px] text-white font-bold">✓</span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Download button inside the right section */}
                  <div className="mt-4 pt-3 border-t border-ui-hairline">
                    <button
                      type="button"
                      onClick={downloadQR}
                      className="w-full flex items-center justify-center gap-2 py-2.5 border border-ui-success text-ui-success rounded-sm text-xs font-semibold hover:bg-ui-success/5 transition-colors"
                    >
                      <Download size={14} /> Download QR Code
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              /* Empty state (when generatedLink is false) */
              <div className="flex flex-col items-center justify-center min-h-[260px] py-4">
                <div className="flex flex-col items-center gap-3 text-center px-6">
                  <div className="w-16 h-16 rounded-xl bg-ui-success/10 border border-ui-success/20 flex items-center justify-center">
                    <QRIcon size={32} color="#10b981" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-ui-ink">Your QR Code appears here</p>
                    <p className="text-xs text-ui-muted mt-1 leading-relaxed">
                      Fill in your WhatsApp number on the left and click<br />"Generate" to instantly create your shareable link and QR code.
                    </p>
                  </div>
                  {/* Static style preview grid for empty state */}
                  <div className="w-full mt-2">
                    <p className="text-[10px] font-bold text-ui-muted uppercase tracking-wider mb-2">Available styles</p>
                    <div className="grid grid-cols-4 gap-2">
                      {QR_THEMES.map(({ id, label, Thumb }) => (
                        <div key={id} className="flex flex-col items-center gap-1">
                          <div className="w-full h-14 border border-ui-hairline rounded-sm overflow-hidden bg-ui-surface-soft p-1">
                            <Thumb active={false} />
                          </div>
                          <span className="text-[9px] text-ui-muted text-center leading-tight">{label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </FormCard>
          
          {/* Step 2 — Generated link (shown after generate) */}
          <AnimatePresence>
            {generatedLink && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.25 }}
              >
                <FormCard title={<span className="flex items-center gap-2"><StepBadge n={3} /> Your generated link</span>}>
                  <div className="space-y-3">
                    {/* Link copy row */}
                    <div className="flex items-center gap-2 border border-ui-hairline rounded-sm overflow-hidden bg-ui-surface-soft">
                      <span className="flex-1 px-3 text-xs text-ui-body truncate font-mono py-2.5">
                        {generatedLink}
                      </span>
                      <button
                        type="button"
                        onClick={copyLink}
                        className={cn(
                          "flex items-center gap-1.5 px-4 py-2.5 text-xs font-bold transition-all shrink-0",
                          copied ? "bg-ui-success text-white" : "bg-ui-success text-white hover:bg-emerald-600"
                        )}
                      >
                        {copied ? <><CopyCheck size={13} /> Copied</> : <><Copy size={13} /> Copy</>}
                      </button>
                    </div>

                  </div>
                </FormCard>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
